import React, { useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { SubmitHandler, FormHandles } from "@unform/core";
import { Form } from "@unform/web";
import * as Yup from "yup";

import { Input } from "components/shared/Form";
import { useTranslation, useValidation } from "hooks";
import { translations } from "./translations";
import * as S from "./styles";

import {
  CreateCarrierResponsibleEmailActions,
  CreateCarrierResponsibleEmailState,
  DeleteCarrierResponsibleEmailActions,
  DeleteCarrierResponsibleEmailState,
  PaginateCarrierResponsibleEmailActions,
  PaginateCarrierResponsibleEmailState,
} from "store/ducks/settings/carrier-responsible-email";

interface IProps {
  general_carrier_id: number | string;
}

export const ResponsibleEmails: React.FC<IProps> = ({
  general_carrier_id,
}) => {
  const dispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { getTranslation } = useTranslation(translations);
  const { handleFormErrors } = useValidation();

  const { loading: loadingCreate } = useSelector<RootStateOrAny>(
    (state) => state.createCarrierResponsibleEmail
  ) as CreateCarrierResponsibleEmailState;

  const { loading: loadingDelete } = useSelector<RootStateOrAny>(
    (state) => state.deleteCarrierResponsibleEmail
  ) as DeleteCarrierResponsibleEmailState;

  const { data } = useSelector<RootStateOrAny>(
    (state) => state.paginateCarrierResponsibleEmail
  ) as PaginateCarrierResponsibleEmailState;

  const getData = useCallback(() => {
    dispatch(
      PaginateCarrierResponsibleEmailActions.request({ general_carrier_id })
    );
  }, [dispatch, general_carrier_id]);

  const onSuccess = useCallback(() => {
    formRef.current?.reset();
    getData();
  }, [getData]);

  const handleSubmit = useCallback<SubmitHandler>(
    async (formData) => {
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          email: Yup.string()
            .email(getTranslation("emailInvalido"))
            .required(getTranslation("obrigatorio")),
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

        dispatch(
          CreateCarrierResponsibleEmailActions.request(
            {
              data: [
                {
                  email: formData.email,
                  general_carrier_id,
                },
              ],
            },
            onSuccess
          )
        );
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, general_carrier_id, getTranslation, handleFormErrors, onSuccess]
  );

  const handleDelete = useCallback(
    (id: number) => {
      dispatch(DeleteCarrierResponsibleEmailActions.request(id, getData));
    },
    [dispatch, getData]
  );

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <S.Container>
      <S.Title>{getTranslation("titulo")}</S.Title>
      <S.BoxContainer>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <S.FormRow>
            <Input
              name="email"
              label={getTranslation("email")}
              placeholder={getTranslation("placeholder")}
            />
            <S.ButtonContainerCreate>
              <S.ButtonMini
                type="button"
                className="add"
                onClick={() => formRef.current?.submitForm()}
              >
                {loadingCreate ? <S.Loading /> : "+"}
              </S.ButtonMini>
            </S.ButtonContainerCreate>
          </S.FormRow>
        </Form>

        <S.GridContainer>
          {!data.length && (
            <S.EmptyState>{getTranslation("vazio")}</S.EmptyState>
          )}

          {data.map((item: any) => (
            <S.ItemContainer key={item.id}>
              <S.ItemContent>
                <S.ItemValue>{item.email}</S.ItemValue>
                <S.ButtonIcon
                  onClick={() => handleDelete(item.id)}
                  disabled={loadingDelete}
                >
                  <S.IconDelete disabled={loadingDelete} />
                </S.ButtonIcon>
              </S.ItemContent>
            </S.ItemContainer>
          ))}
        </S.GridContainer>
      </S.BoxContainer>
    </S.Container>
  );
};
